

import React, { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function EnvViolationSection({ caseId, sofaResponse }) {
  const [violations, setViolations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newViolation, setNewViolation] = useState({
    site_name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    government_unit: '',
    gov_unit_city: '',
    gov_unit_state: '',
    gov_unit_zip: '',
    environmental_law: '',
    notice_date: ''
  });
  const [editViolation, setEditViolation] = useState(null);

  // Fetch existing violations
  useEffect(() => {
    if (!sofaResponse) return;
    async function fetchViolations() {
      try {
        const res = await fetch(
          `${API_URL}/cases/${caseId}/sofa-gov-env-violations/?sofa_response=${sofaResponse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to fetch violations');
        setViolations(await res.json());
      } catch (err) {
        console.error(err);
      }
    }
    fetchViolations();
  }, [caseId, sofaResponse]);

  // Open modals
  function openAddModal() {
    setNewViolation({
      site_name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      government_unit: '',
      gov_unit_city: '',
      gov_unit_state: '',
      gov_unit_zip: '',
      environmental_law: '',
      notice_date: ''
    });
    setModalOpen(true);
  }
  function openEditModal(item) {
    setEditViolation({ ...item });
    setEditModalOpen(true);
  }

  // Create
  async function handleSaveNew() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-env-violations/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(newViolation)
        }
      );
      if (!res.ok) throw new Error('Failed to add violation');
      const created = await res.json();
      setViolations(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Update
  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-env-violations/${editViolation.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editViolation)
        }
      );
      if (!res.ok) throw new Error('Failed to update violation');
      const updated = await res.json();
      setViolations(prev =>
        prev.map(v => (v.id === updated.id ? updated : v))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  }

  // Delete
  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-gov-env-violations/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error('Failed to delete violation');
      setViolations(prev => prev.filter(v => v.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 24: Environmental Law Violations
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List notices by a governmental unit that you have violated an environmental law.
        </p>
      {violations.length === 0 ? (
        <p>No environmental violation notices recorded.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {violations.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" 
                     className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                           2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                           6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                           2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                           0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                           00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.site_name || '—'}</h3>
              <p className="mt-1 text-sm">
                {item.street1 || ''}{item.street2 ? `, ${item.street2}` : ''}, {item.city || ''}, {item.state || ''} {item.zip_code || ''}
              </p>
              <p className="mt-2 text-sm">
                <strong>Gov Unit:</strong> {item.government_unit || '—'} 
                {item.gov_unit_city && (
                  <> — {item.gov_unit_city}, {item.gov_unit_state} {item.gov_unit_zip}</>
                )}
              </p>
              <p className="mt-2 text-sm">
                <strong>Law:</strong> {item.environmental_law || '—'}
              </p>
              <p className="mt-2 text-sm">
                <strong>Notice Date:</strong> {item.notice_date || '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Violation Notice
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Add Violation Notice</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Site Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newViolation.site_name}
                onChange={e => setNewViolation(prev => ({ ...prev, site_name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street 1"
                className="w-full border rounded px-2 py-1"
                value={newViolation.street1}
                onChange={e => setNewViolation(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street 2"
                className="w-full border rounded px-2 py-1"
                value={newViolation.street2}
                onChange={e => setNewViolation(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.city}
                  onChange={e => setNewViolation(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.state}
                  onChange={e => setNewViolation(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.zip_code}
                  onChange={e => setNewViolation(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Government Unit</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newViolation.government_unit}
                onChange={e => setNewViolation(prev => ({ ...prev, government_unit: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="Gov Unit City"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.gov_unit_city}
                  onChange={e => setNewViolation(prev => ({ ...prev, gov_unit_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="Gov Unit State"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.gov_unit_state}
                  onChange={e => setNewViolation(prev => ({ ...prev, gov_unit_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Gov Unit Zip"
                  className="w-full border rounded px-2 py-1"
                  value={newViolation.gov_unit_zip}
                  onChange={e => setNewViolation(prev => ({ ...prev, gov_unit_zip: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Environmental Law</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newViolation.environmental_law}
                onChange={e => setNewViolation(prev => ({ ...prev, environmental_law: e.target.value }))}
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Notice Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={newViolation.notice_date}
                onChange={e => setNewViolation(prev => ({ ...prev, notice_date: e.target.value }))}
              />
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">
                Cancel
              </button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editViolation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg overflow-y-auto max-h-[90vh]">
            <h3 className="text-lg font-semibold mb-4">Edit Violation Notice</h3>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Site Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editViolation.site_name}
                onChange={e => setEditViolation(prev => ({ ...prev, site_name: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-3">
              <input
                type="text"
                placeholder="Street 1"
                className="w-full border rounded px-2 py-1"
                value={editViolation.street1}
                onChange={e => setEditViolation(prev => ({ ...prev, street1: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Street 2"
                className="w-full border rounded px-2 py-1"
                value={editViolation.street2}
                onChange={e => setEditViolation(prev => ({ ...prev, street2: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="City"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.city}
                  onChange={e => setEditViolation(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="State"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.state}
                  onChange={e => setEditViolation(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Zip"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.zip_code}
                  onChange={e => setEditViolation(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Government Unit</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editViolation.government_unit}
                onChange={e => setEditViolation(prev => ({ ...prev, government_unit: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="Gov Unit City"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.gov_unit_city}
                  onChange={e => setEditViolation(prev => ({ ...prev, gov_unit_city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  placeholder="Gov Unit State"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.gov_unit_state}
                  onChange={e => setEditViolation(prev => ({ ...prev, gov_unit_state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <input
                  type="text"
                  placeholder="Gov Unit Zip"
                  className="w-full border rounded px-2 py-1"
                  value={editViolation.gov_unit_zip}
                  onChange={e => setEditViolation(prev => ({ ...prev, gov_unit_zip: e.target.value }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Environmental Law</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editViolation.environmental_law}
                onChange={e => setEditViolation(prev => ({ ...prev, environmental_law: e.target.value }))}
              />
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Notice Date</label>
              <input
                type="date"
                className="w-full border rounded px-2 py-1"
                value={editViolation.notice_date}
                onChange={e => setEditViolation(prev => ({ ...prev, notice_date: e.target.value }))}
              />
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}